import {
  AutomaticApplySkipReason,
  IAutomaticApplySkip,
} from "./automaticApplySkipReason";

export interface IAutomaticApplyHandoffClient {
  organizationId: number;
  utmSources: string[];
}

export interface IAutomaticApplyHandoffConfig {
  clients: IAutomaticApplyHandoffClient[];
  excludedUserAgents: string[];
}

/** Returns the configured pattern the user agent matched, if any. */
export function findExcludedUserAgent(
  userAgent: string | null | undefined,
  excludedUserAgents: string[]
): string | undefined {
  const normalizedUserAgent = (userAgent ?? "").toLowerCase();
  return excludedUserAgents.find((agent) => {
    const normalizedAgent = agent.toLowerCase().trim();
    return !!normalizedAgent && normalizedUserAgent.includes(normalizedAgent);
  });
}

/**
 * Mirrors the branch `navigateToJobApplication` uses to decide between a
 * mailto: hand-off and opening the employer's form, so the two cannot disagree.
 *
 * These jobs are excluded from the automatic hand-off: replacing the tab with a
 * mailto: opens the candidate's mail client and strands them on a blank page
 * with nothing to go back to.
 */
export function isEmailApplicationJob(params: {
  applyUrl: string | null | undefined;
  isApplyViaEmail: boolean | null | undefined;
  applyEmail: string | null | undefined;
  sourceInternalId: string | null | undefined;
}): boolean {
  const { applyUrl, isApplyViaEmail, applyEmail, sourceInternalId } = params;
  const appliesViaEmail =
    !!isApplyViaEmail && !!applyEmail && (sourceInternalId ?? "") === "";
  return appliesViaEmail || /^mailto:/i.test(applyUrl ?? "");
}

/**
 * The hand-off replaces the tab, so it can only run against a real web address.
 * Guards the case where a job carries no apply_url and the built string is the
 * literal "undefined".
 */
export function isNavigableApplyUrl(
  applyUrl: string | null | undefined
): boolean {
  return /^https?:\/\//i.test((applyUrl ?? "").trim());
}

export type AutomaticApplyEvaluation =
  | { eligible: true }
  | ({ eligible: false } & IAutomaticApplySkip);

/**
 * Decides the hand-off and, when it does not happen, says why. The first
 * failing check wins, so a crawler viewing an email job records only the
 * crawler.
 */
export function evaluateAutomaticApply(params: {
  clients: IAutomaticApplyHandoffClient[];
  excludedUserAgents: string[];
  organizationId: number;
  utmSource: string | null | undefined;
  userAgent: string | null | undefined;
  applyUrl: string | null | undefined;
  isApplyViaEmail: boolean | null | undefined;
  applyEmail: string | null | undefined;
  sourceInternalId: string | null | undefined;
}): AutomaticApplyEvaluation {
  const {
    clients,
    excludedUserAgents,
    organizationId,
    utmSource,
    userAgent,
    applyUrl,
    isApplyViaEmail,
    applyEmail,
    sourceInternalId,
  } = params;
  const excludedUserAgent = findExcludedUserAgent(
    userAgent,
    excludedUserAgents
  );
  if (excludedUserAgent !== undefined) {
    return {
      eligible: false,
      reason: AutomaticApplySkipReason.EXCLUDED_USER_AGENT,
      detail: excludedUserAgent,
    };
  }
  if (
    isEmailApplicationJob({
      applyUrl,
      isApplyViaEmail,
      applyEmail,
      sourceInternalId,
    })
  ) {
    return {
      eligible: false,
      reason: AutomaticApplySkipReason.EMAIL_APPLICATION,
      detail: /^mailto:/i.test(applyUrl ?? "")
        ? "mailto_apply_url"
        : "apply_via_email",
    };
  }
  if (!isNavigableApplyUrl(applyUrl)) {
    return {
      eligible: false,
      reason: AutomaticApplySkipReason.APPLY_URL_NOT_NAVIGABLE,
      detail: (applyUrl ?? "").slice(0, 200),
    };
  }
  const normalizedUtmSource = (utmSource ?? "").toLowerCase().trim();
  if (!normalizedUtmSource) {
    return {
      eligible: false,
      reason: AutomaticApplySkipReason.BLANK_UTM_SOURCE,
    };
  }
  const client = clients.find(
    (entry) => entry.organizationId === organizationId
  );
  if (!client) {
    return {
      eligible: false,
      reason: AutomaticApplySkipReason.ORGANIZATION_NOT_CONFIGURED,
    };
  }
  if (!client.utmSources.includes(normalizedUtmSource)) {
    return {
      eligible: false,
      reason: AutomaticApplySkipReason.UTM_SOURCE_NOT_CONFIGURED,
      detail: `"${normalizedUtmSource}" not in [${client.utmSources.join(
        ", "
      )}]`,
    };
  }
  return { eligible: true };
}
